/**
 * CoachSimpleLauncher (iter121) — Large-tile launcher for coaches in
 * Simplified Mode. Mirrors the Apple-style student launcher but wired to
 * coach-specific destinations. Tile set is picked by the coach from their
 * profile (up to 8 tiles); admin-only tiles are filtered out for non-admins.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarClock, Users, ClipboardCheck, CalendarCheck, Package,
  Bell, Users2, PoundSterling, ShieldCheck, BarChart3, Settings2,
  MessageSquareText, PartyPopper,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import InstallAppPrompt from "@/components/InstallAppPrompt";
import "./SimpleLauncher.css";

// Full catalogue of tiles a coach can pin. Keys must match the backend
// ALLOWED_SIMPLIFIED_TILES set. `adminOnly` tiles are hidden for coaches
// without admin permissions.
export const COACH_TILE_CATALOG = [
  {
    key: "today",
    to: "/calendar",
    label: "Today's Sessions",
    hint: "Your schedule at a glance.",
    Icon: CalendarClock,
    accent: "#CCFF00",
  },
  {
    key: "students",
    to: "/players",
    label: "My Students",
    hint: "Roster, ratings and player notes.",
    Icon: Users,
    accent: "#66C2FF",
  },
  {
    key: "assessments",
    to: "/players#awaiting-feedback",
    label: "Assessments",
    hint: "Mark attended, rate skills, log focus.",
    Icon: ClipboardCheck,
    accent: "#FF9F1C",
  },
  {
    // Iter155 — Post-session feedback queue. Badge count is hydrated by the
    // launcher from GET /me/coach/pending-feedback so coaches know at a
    // glance how many players are still waiting for their recap.
    key: "feedback_needed",
    to: "/coach/feedback",
    label: "Feedback Needed",
    hint: "Log per-topic notes + email AI recap.",
    Icon: MessageSquareText,
    accent: "#FF4500",
  },
  {
    key: "availability",
    to: "/availability",
    label: "Set Availability",
    hint: "Publish windows for new bookings.",
    Icon: CalendarCheck,
    accent: "#007AFF",
  },
  {
    key: "offerings",
    to: "/offerings",
    label: "Offerings",
    hint: "Groups, 1:1, Performance sessions.",
    Icon: Package,
    accent: "#CCFF00",
  },
  {
    key: "messages",
    to: "/notifications",
    label: "Messages",
    hint: "Inbox, requests and updates.",
    Icon: Bell,
    accent: "#FFD700",
  },
  {
    key: "group_requests",
    to: "/group-requests",
    label: "Legacy Requests",
    hint: "Quote-based flow (older).",
    Icon: Users2,
    accent: "#525252",
  },
  {
    // Iter124 — Session Requests tile: the new player-organised session
    // queue (solo/group/club). Coach sees student-organised requests +
    // can approve, price override, and lock the exact date/time on the
    // coach dashboard.
    key: "club_requests",
    to: "/group-requests",
    label: "Session Requests",
    hint: "Student-organised solo · group · club bookings.",
    Icon: Users2,
    accent: "#FF4500",
  },
  {
    key: "pricing",
    to: "/settings/pricing",
    label: "Pricing",
    hint: "Rates for group · performance · 1:1.",
    Icon: PoundSterling,
    accent: "#FFD700",
  },
  {
    key: "admin",
    to: "/admin",
    label: "Admin Console",
    hint: "All admin tools in one place.",
    Icon: ShieldCheck,
    accent: "#CCFF00",
    adminOnly: true,
  },
  {
    key: "stats",
    to: "/admin/stats",
    label: "Stats & KPIs",
    hint: "Users · bookings · revenue.",
    Icon: BarChart3,
    accent: "#66C2FF",
    adminOnly: true,
  },
  {
    // Iter179 — Rally-Lab Festival Live Ops. Available to every coach (any
    // organiser can spin up a festival), sits alongside the coaching tiles.
    key: "festival",
    to: "/festival/mine",
    label: "Festivals",
    hint: "Run live pickleball festivals · v1.0.",
    Icon: PartyPopper,
    accent: "#CCFF00",
  },
];

// Default 5-tile set for coaches who haven't customised their launcher.
// Iter180 — added `festival` so the Rally-Lab Festival Live Ops module is
// discoverable from Simple Mode out of the box.
export const DEFAULT_COACH_TILES = ["today", "students", "feedback_needed", "assessments", "availability", "club_requests", "festival"];

export default function CoachSimpleLauncher({ onExitSimplified }) {
  const { user } = useAuth();
  const firstName = (user?.name || "").split(" ")[0] || "coach";
  const perms = new Set(user?.permissions || []);
  if (user?.role === "admin") perms.add("admin");
  const isAdmin = perms.has("admin") || perms.has("super_admin");

  const configured = Array.isArray(user?.simplified_tiles) && user.simplified_tiles.length > 0
    ? user.simplified_tiles
    : DEFAULT_COACH_TILES;

  // Iter155 — hydrate the "Feedback Needed" tile with a live count badge.
  // If the coach has never configured the launcher, DEFAULT_COACH_TILES
  // already surfaces the tile. Otherwise we auto-inject it at the top when
  // there's pending work so nothing gets buried behind stale customisation.
  const [pendingCount, setPendingCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    api.get("/me/coach/pending-feedback")
      .then((r) => { if (!cancelled) setPendingCount(Number(r.data?.count || 0)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const effectiveTiles = (() => {
    let tiles = configured;
    // Iter155 — inject "feedback_needed" when there's pending work.
    if (pendingCount > 0 && !tiles.includes("feedback_needed")) {
      tiles = ["feedback_needed", ...tiles];
    }
    // Iter180 — backfill Festivals tile for coaches whose simplified_tiles
    // list was saved before the tile existed. Skipped when they've clearly
    // customised recently (>8 tiles never happens; we just append).
    if (!tiles.includes("festival")) {
      tiles = [...tiles, "festival"];
    }
    return tiles;
  })();

  const byKey = new Map(COACH_TILE_CATALOG.map((t) => [t.key, t]));
  const tiles = effectiveTiles
    .map((k) => byKey.get(k))
    .filter(Boolean)
    .filter((t) => (t.adminOnly ? isAdmin : true))
    .slice(0, 8);

  return (
    <div className="simple-launcher" data-testid="coach-simple-launcher">
      <header className="simple-launcher__hero">
        <div className="simple-launcher__eyebrow">COACH CONSOLE · SIMPLIFIED</div>
        <h1 className="simple-launcher__title">
          Hey {firstName}. <span className="accent">Where to?</span>
        </h1>
        <p className="simple-launcher__sub">
          Big tiles, zero clutter. Head to <strong>Profile → Simplified Mode</strong> to pick which tiles you see.
        </p>
        <InstallAppPrompt />
      </header>

      <div className="simple-launcher__grid" role="menu" aria-label="Coach navigation tiles">
        {tiles.map(({ key, to, label, hint, Icon, accent }) => {
          const showBadge = key === "feedback_needed" && pendingCount > 0;
          return (
            <Link
              key={key}
              to={to}
              data-testid={`coach-simple-tile-${key}`}
              role="menuitem"
              className="simple-tile relative"
              style={{ "--tile-accent": accent }}
            >
              <span className="simple-tile__icon" aria-hidden>
                <Icon strokeWidth={1.5} />
              </span>
              <span className="simple-tile__label">{label}</span>
              <span className="simple-tile__hint">{hint}</span>
              <span className="simple-tile__arrow" aria-hidden>→</span>
              {showBadge && (
                <span
                  data-testid="feedback-needed-badge"
                  className="absolute top-3 right-3 min-w-[26px] h-[26px] px-1.5 flex items-center justify-center rounded-full text-xs font-bold"
                  style={{ background: "#FF4500", color: "#0A0A0A" }}
                >
                  {pendingCount > 99 ? "99+" : pendingCount}
                </span>
              )}
            </Link>
          );
        })}
      </div>

      <footer className="simple-launcher__foot">
        <div className="simple-launcher__foot-actions">
          <button
            type="button"
            onClick={onExitSimplified}
            data-testid="coach-switch-to-full-btn"
            className="simple-launcher__switch"
          >
            <Settings2 className="w-4 h-4" strokeWidth={1.75} />
            Switch to Full Dashboard
          </button>
          <Link
            to="/coach/profile#simplified-mode"
            data-testid="coach-customise-tiles-btn"
            className="simple-launcher__switch"
          >
            Customise tiles →
          </Link>
        </div>
        <p className="simple-launcher__disclaimer">
          Full Dashboard shows the whole console, KPIs, admin shortcuts and diary. Switch back anytime.
        </p>
      </footer>
    </div>
  );
}
